import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { apiService, VendorAnalytics } from '../../services/api';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { WorkspaceChart } from '../WorkspaceChart';
import type { ChartData } from '../../utils/workspaceChart';
import { ANALYTICS_STATUSES, ORDER_STATUSES, changePercent, formatMoney, periodRange } from '../../utils/vendorPeriods';
import { PeriodValue, VendorPeriodBar } from './VendorPeriodBar';

const STATUS_TONE: Record<string, 'good' | 'warn' | 'bad' | undefined> = { delivered: 'good', processing: 'warn', cancelled: 'bad' };

/** A vendor's performance for a period, compared with the period before it. */
export const VendorAnalyticsTab: React.FC<{ vendorId: string }> = ({ vendorId }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const [period, setPeriod] = useState<PeriodValue>({ period: '30d' });
  const [statuses, setStatuses] = useState<string[]>(ANALYTICS_STATUSES);
  const [data, setData] = useState<VendorAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metric, setMetric] = useState<'revenue' | 'orders' | 'units'>('revenue');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const range = period.period === 'all' ? periodRange('custom', { from: new Date(2000, 0, 1), to: new Date() }) : periodRange(period.period, period);
      const res = await apiService.getVendorAnalytics(vendorId, { ...range, statuses });
      if (res.statusCode === 1000 && res.data) setData(res.data);
      else setError((res as any).message || t('vendors.analytics.loadFailed'));
    } catch (e: any) {
      setError(e?.message || t('vendors.analytics.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [vendorId, period, statuses]);

  useEffect(() => { load(); }, [load]);

  const money = (n: number) => formatMoney(n, data?.currency, locale);
  const num = (n: number) => { try { return n.toLocaleString(locale, { maximumFractionDigits: 2 }); } catch { return String(n); } };

  const kpi = (icon: string, label: string, value: string, current: number, previous: number | undefined) => {
    const change = previous === undefined || period.period === 'all' ? undefined : changePercent(current, previous);
    return (
      <View style={styles.kpi} key={label}>
        <View style={[styles.kpiTop, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name={icon as any} size={16} color={colors.primary as string} />
          <Text style={[styles.kpiLabel, { textAlign: rtl.textStart }]} numberOfLines={1}>{label}</Text>
        </View>
        <Text style={[styles.kpiValue, { textAlign: rtl.textStart }]} numberOfLines={1}>{value}</Text>
        {change !== undefined && (
          <Text style={[styles.kpiChange, { textAlign: rtl.textStart, color: (change >= 0 ? colors.success : colors.error) as string }]}>
            {change >= 0 ? '▲' : '▼'} {Math.abs(change)}% {t('vendors.analytics.vsPrevious')}
          </Text>
        )}
      </View>
    );
  };

  const toggleStatus = (s: string) => setStatuses((prev) => {
    const next = prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s];
    return next.length ? next : ANALYTICS_STATUSES;
  });

  const seriesChart: ChartData | undefined = data && data.series.points.length ? {
    type: 'bar',
    title: t(`vendors.analytics.over.${metric}` as any),
    items: data.series.points.map((p) => ({ label: dateLabel(p.date, data.series.bucket, locale), value: p[metric] })),
    max: Math.max(...data.series.points.map((p) => p[metric]), 0),
  } : undefined;

  const productsChart: ChartData | undefined = data && data.topProducts.length ? {
    type: 'bar',
    title: t('vendors.analytics.topProducts'),
    items: data.topProducts.map((p) => ({ label: `${p.name} · ${t('vendors.orders.units', { count: p.units })}`, value: p.revenue })),
    max: Math.max(...data.topProducts.map((p) => p.revenue), 0),
  } : undefined;

  const statusItems = data ? ORDER_STATUSES.filter((s) => data.byStatus[s]).map((s) => ({ label: t(`orders.status.${s}` as any), value: data.byStatus[s], ...(STATUS_TONE[s] ? { tone: STATUS_TONE[s] } : {}) })) : [];
  const statusChart: ChartData | undefined = statusItems.length > 1 ? { type: 'pie', title: t('vendors.analytics.byStatus'), items: statusItems, max: 0 } : undefined;

  return (
    <View style={styles.wrap}>
      <VendorPeriodBar value={period} onChange={setPeriod} />
      <View style={[styles.statusRow, { flexDirection: rtl.rowDirection }]}>
        <Text style={styles.statusLabel}>{t('vendors.analytics.counting')}</Text>
        {ORDER_STATUSES.filter((s) => s !== 'pending').map((s) => (
          <TouchableOpacity key={s} style={[styles.chip, statuses.includes(s) && styles.chipActive]} onPress={() => toggleStatus(s)}>
            <Text style={[styles.chipText, statuses.includes(s) && styles.chipTextActive]}>{t(`orders.status.${s}` as any)}</Text>
          </TouchableOpacity>
        ))}
      </View>

      {loading && !data ? <ActivityIndicator style={{ marginVertical: spacing.xl }} color={colors.primary as string} /> : null}
      {!!error && <Text style={[styles.error, { textAlign: rtl.textStart }]}>{error}</Text>}

      {data && (
        <View style={[styles.content, loading && styles.dim]}>
          <View style={[styles.kpis, { flexDirection: rtl.rowDirection }]}>
            {kpi('cash-outline', t('vendors.analytics.revenue'), money(data.totals.revenue), data.totals.revenue, data.previous.revenue)}
            {kpi('receipt-outline', t('vendors.analytics.orders'), num(data.totals.orders), data.totals.orders, data.previous.orders)}
            {kpi('cube-outline', t('vendors.analytics.units'), num(data.totals.units), data.totals.units, data.previous.units)}
            {kpi('stats-chart-outline', t('vendors.analytics.averageOrder'), money(data.totals.averageOrder), data.totals.averageOrder, data.previous.averageOrder)}
            {kpi('people-outline', t('vendors.analytics.customers'), num(data.totals.customers), data.totals.customers, data.previous.customers)}
            {data.minimum && kpi('flag-outline', t('vendors.analytics.minimumMet'), `${data.minimum.met} / ${data.minimum.total}`, data.minimum.met, undefined)}
          </View>
          {data.minimum && (
            <Text style={[styles.note, { textAlign: rtl.textStart }]}>
              {t('vendors.analytics.minimumNote', { amount: formatMoney(data.minimum.amount, data.minimum.currency, locale), percent: data.minimum.total ? Math.round((data.minimum.met / data.minimum.total) * 100) : 0 })}
            </Text>
          )}

          {data.totals.orders === 0 ? (
            <View style={styles.empty}>
              <Ionicons name="analytics-outline" size={40} color={colors.text.disabled as string} />
              <Text style={styles.emptyText}>{t('vendors.analytics.empty')}</Text>
            </View>
          ) : (
            <>
              <View style={styles.card}>
                <View style={[styles.metricRow, { flexDirection: rtl.rowDirection }]}>
                  {(['revenue', 'orders', 'units'] as const).map((m) => (
                    <TouchableOpacity key={m} style={[styles.chip, metric === m && styles.chipActive]} onPress={() => setMetric(m)}>
                      <Text style={[styles.chipText, metric === m && styles.chipTextActive]}>{t(`vendors.analytics.${m}` as any)}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {seriesChart && <WorkspaceChart data={seriesChart} isRTL={rtl.isRTL} format={metric === 'revenue' ? money : num} />}
              </View>
              {productsChart && <View style={styles.card}><WorkspaceChart data={productsChart} isRTL={rtl.isRTL} format={money} /></View>}
              {statusChart && <View style={styles.card}><WorkspaceChart data={statusChart} isRTL={rtl.isRTL} format={num} /></View>}
            </>
          )}
          {data.truncated && <Text style={[styles.note, { textAlign: rtl.textStart }]}>{t('vendors.analytics.truncated')}</Text>}
        </View>
      )}
    </View>
  );
};

function dateLabel(key: string, bucket: 'day' | 'week' | 'month', locale: string): string {
  const [y, m, d] = key.split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  try {
    return bucket === 'month'
      ? date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
      : date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  } catch {
    return key;
  }
}

const getStyles = () => StyleSheet.create({
  wrap: { gap: spacing.sm },
  statusRow: { alignItems: 'center', gap: spacing.xs, flexWrap: 'wrap' },
  statusLabel: { fontSize: typography.fontSize.xs, color: colors.text.secondary, fontWeight: '700' },
  chip: { paddingVertical: 4, paddingHorizontal: spacing.sm + 2, borderRadius: 999, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface },
  chipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  chipText: { fontSize: 11, color: colors.text.primary, fontWeight: '600' },
  chipTextActive: { color: colors.white },
  error: { color: colors.error, fontSize: typography.fontSize.sm },
  content: { gap: spacing.md },
  dim: { opacity: 0.6 },
  kpis: { flexWrap: 'wrap', gap: spacing.sm },
  kpi: { flexGrow: 1, flexBasis: 150, padding: spacing.md, borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface, gap: 4 },
  kpiTop: { alignItems: 'center', gap: 6 },
  kpiLabel: { flex: 1, fontSize: typography.fontSize.xs, color: colors.text.secondary, fontWeight: '600' },
  kpiValue: { fontSize: typography.fontSize.xl, fontWeight: '700', color: colors.text.primary },
  kpiChange: { fontSize: 11, fontWeight: '600' },
  note: { fontSize: typography.fontSize.xs, color: colors.text.secondary },
  card: { borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.border, backgroundColor: colors.background.surface, overflow: 'hidden' },
  metricRow: { gap: spacing.xs, padding: spacing.sm, paddingBottom: 0 },
  empty: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xl },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, textAlign: 'center' },
});
